/*
This component displays a pie chart of competitor mentions for a selected topic.
It fetches data from the API and allows filtering by time range.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, Sector } from 'recharts';
import TimeRangeSelector from './TimeRangeSelector';
import ProductSelector from './ProductSelector';
import { subYears, format } from 'date-fns';
import '../styles/CompetitorMentionsChart.css';
import { useAuth } from './AuthContext';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';

// Enhanced color palette to match the image
const COLORS = [
    '#0088FE',  // Bright blue for Cursor
    '#00C49F',  // Teal for Cline
    '#FFBB28',  // Yellow for Github Copilot
    '#FF8042',  // Orange for Claude
    '#8884d8',  // Purple for Aider
    '#82ca9d',  // Green
    '#ffc658',  // Light yellow
    '#ff7300',  // Dark orange
    '#a4de6c',  // Light green
    '#d0ed57'   // Lime
];

// Special color for the "Other" category - lighter gray to match image
const OTHER_COLOR = '#E8E8E8';

const CompetitorMentionsChart = ({ topics, assessmentData }) => {
    const { flaskServerAddress } = useAuth();
    const [timeRange, setTimeRange] = useState('3y');
    const [selectedProduct, setSelectedProduct] = useState("");
    const [competitorMentions, setCompetitorMentions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);
    const [activeIndex, setActiveIndex] = useState(null);
    const [hiddenCompetitors, setHiddenCompetitors] = useState(new Set());
    const [useAssessmentData, setUseAssessmentData] = useState(false);
    const [filteredTopicMap, setFilteredTopicMap] = useState({});

    // Debug logging
    /* console.log('CompetitorMentionsChart props:', { 
        topicsLength: topics?.length,
        assessmentDataLength: assessmentData?.length,
    }); */

    // Filter topics to only include those with competitive comparison data
    useEffect(() => {
        if (!topics || !assessmentData) return;
        
        // Group assessmentData by topic_id
        const assessmentsByTopic = {};
        assessmentData.forEach(item => {
            if (item.topic_id) {
                if (!assessmentsByTopic[item.topic_id]) {
                    assessmentsByTopic[item.topic_id] = [];
                }
                assessmentsByTopic[item.topic_id].push(item);
            }
        });
        
        // Filter topics to only include those with assessment data
        const validTopics = topics.filter(topic => 
            assessmentsByTopic[topic.id] && assessmentsByTopic[topic.id].length > 0
        );
        
        // Convert to topicMap format for ProductSelector
        const topicMap = validTopics.reduce((acc, topic) => {
            acc[topic.id] = {
                id: topic.id,
                name: topic.topic || topic.name,
                priority: topic.priority
            };
            return acc;
        }, {});
        
        setFilteredTopicMap(topicMap);
        
        // Select default product if none is selected and we have topics
        if (!selectedProduct && Object.keys(topicMap).length > 0) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB; // Sort by priority (lowest first)
            });
            
            if (sortedTopics[0]) {
                // console.log('Auto-selecting first topic:', sortedTopics[0]);
                setSelectedProduct(sortedTopics[0].id);
            }
        }
    }, [topics, assessmentData, selectedProduct]);

    // Calculate date range based on selected time range
    const dateRange = useMemo(() => {
        const now = new Date();
        let startDate;
        
        switch(timeRange) {
            case '1y':
                startDate = subYears(now, 1);
                break;
            case '3y':
                startDate = subYears(now, 3);
                break;
            case '5y':
                startDate = subYears(now, 5);
                break;
            case 'all':
            default:
                startDate = new Date(0); // Beginning of time
                break;
        }
        
        return {
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(now, 'yyyy-MM-dd')
        };
    }, [timeRange]);

    // Get filtered assessments for selected product
    const selectedAssessmentData = useMemo(() => {
        if (!assessmentData) return null;
        
        if (!selectedProduct) return assessmentData;
        
        // Filter by selected topic if a product is selected
        return assessmentData.filter(item => 
            item.topic_id === selectedProduct
        );
    }, [selectedProduct, assessmentData]);

    // Process assessment data if available
    useEffect(() => {
        const processAssessmentData = () => {
            if (!selectedAssessmentData || !Array.isArray(selectedAssessmentData) || selectedAssessmentData.length === 0) {
                setUseAssessmentData(false);
                return;
            }

            // console.log('Processing assessment data for competitor mentions:', selectedAssessmentData.length);
            setUseAssessmentData(true);
            setLoading(true);
            setError(null);

            try {
                // Filter by time range
                const now = new Date();
                const filteredData = selectedAssessmentData.filter(item => {
                    if (!item?.time) return false;
                    
                    const date = new Date(item.time * 1000);
                    if (timeRange === 'all') return true;
                    
                    const years = parseInt(timeRange);
                    return date >= subYears(now, years);
                });

                // Group by competitor (using comparator field)
                const competitorCounts = {};
                
                filteredData.forEach(item => {
                    const competitor = item.comparator || item.details?.competitor;
                    if (!competitor) return;
                    
                    if (!competitorCounts[competitor]) {
                        competitorCounts[competitor] = 0;
                    }
                    competitorCounts[competitor]++;
                });

                // Calculate totals and percentages
                const totalMentions = Object.values(competitorCounts).reduce((sum, count) => sum + count, 0);
                
                if (totalMentions === 0) {
                    setCompetitorMentions([]);
                    setLoading(false);
                    return;
                }

                // Convert to array format for the chart
                let mentions = Object.entries(competitorCounts).map(([competitor, count]) => ({
                    competitor_name: competitor,
                    mention_count: count,
                    mention_percentage: (count / totalMentions) * 100
                }));

                // Sort by count descending
                mentions.sort((a, b) => b.mention_count - a.mention_count);

                // Group smaller mentions into "Other" category (anything less than 5%)
                const MIN_PERCENTAGE = 5.0;
                const mainMentions = mentions.filter(mention => mention.mention_percentage >= MIN_PERCENTAGE);
                const otherMentions = mentions.filter(mention => mention.mention_percentage < MIN_PERCENTAGE);
                
                if (otherMentions.length > 0) {
                    const otherCount = otherMentions.reduce((sum, item) => sum + item.mention_count, 0);
                    const otherPercentage = (otherCount / totalMentions) * 100;
                    
                    mainMentions.push({
                        competitor_name: 'Other',
                        mention_count: otherCount,
                        mention_percentage: otherPercentage
                    });
                }

                setCompetitorMentions(mainMentions);
            } catch (err) {
                console.error('Error processing assessment data:', err);
                setError('Failed to process competitor mentions data');
                setCompetitorMentions([]);
            } finally {
                setLoading(false);
                setInitialLoad(false);
            }
        };

        if (selectedAssessmentData && Array.isArray(selectedAssessmentData) && selectedAssessmentData.length > 0) {
            processAssessmentData();
        }
    }, [selectedAssessmentData, timeRange, selectedProduct]);

    // Fetch competitor mentions from API when not using assessment data
    useEffect(() => {
        const fetchCompetitorMentions = async () => {
            if (useAssessmentData) return;
            
            if (!selectedProduct) {
                if (!initialLoad) {
                    setCompetitorMentions([]);
                }
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // Log the API URL being called
                const apiUrl = `${flaskServerAddress}competitor-mentions/${selectedProduct}/`;
                // console.log('Calling API endpoint:', apiUrl);
                
                // Call the new API endpoint with time range parameters
                const response = await axios.get(apiUrl, {
                    params: {
                        min_percentage: 5.0, // Minimum percentage threshold for grouping
                        start_date: dateRange.start_date,
                        end_date: dateRange.end_date
                    }
                });

                // console.log('API response:', response.data);

                if (response.data.success) {
                    setCompetitorMentions(response.data.data);
                } else {
                    // console.error('API returned error:', response.data.error);
                    setError(response.data.error || 'Failed to fetch competitor mentions');
                    setCompetitorMentions([]);
                }
            } catch (err) {
                console.error('Error fetching competitor mentions:', err);
                // Log more detailed error information
                if (err.response) {
                    console.error('Error response data:', err.response.data);
                    console.error('Error response status:', err.response.status);
                    console.error('Error response headers:', err.response.headers);
                } else if (err.request) {
                    console.error('Error request:', err.request);
                } else {
                    // console.error('Error message:', err.message);
                }
                setError('Failed to fetch competitor mentions');
                setCompetitorMentions([]);
            } finally {
                setLoading(false);
                setInitialLoad(false);
            }
        };

        fetchCompetitorMentions();
    }, [selectedProduct, flaskServerAddress, initialLoad, dateRange, timeRange, useAssessmentData]);

    // Calculate visible competitor mentions and adjust percentages
    const visibleCompetitorMentions = useMemo(() => {
        if (!competitorMentions || competitorMentions.length === 0) return [];
        
        // Filter out hidden competitors
        const visibleMentions = competitorMentions.filter(
            competitor => !hiddenCompetitors.has(competitor.competitor_name)
        );

        // Recalculate total mentions for visible competitors
        const visibleTotal = visibleMentions.reduce((sum, item) => sum + item.mention_count, 0);

        // Recalculate percentages based on visible total
        return visibleMentions.map(mention => ({
            ...mention,
            mention_percentage: (mention.mention_count / visibleTotal) * 100
        }));
    }, [competitorMentions, hiddenCompetitors]);

    // Sort competitor mentions to ensure "Other" is always last
    const sortedCompetitorMentions = useMemo(() => {
        if (!visibleCompetitorMentions || visibleCompetitorMentions.length === 0) return [];
        
        return [...visibleCompetitorMentions].sort((a, b) => {
            if (a.competitor_name === 'Other') return 1;
            if (b.competitor_name === 'Other') return -1;
            return b.mention_count - a.mention_count;
        });
    }, [visibleCompetitorMentions]);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="custom-tooltip">
                    <p className="competitor-name">{data.competitor_name}</p>
                    <p className="mention-count">{data.mention_count} mentions</p>
                    <p className="mention-percentage">
                        {data.mention_percentage.toFixed(1)}% of all mentions
                    </p>
                </div>
            );
        }
        return null;
    };

    // Handle pie sector hover
    const onPieEnter = (_, index) => {
        setActiveIndex(index);
    };

    const onPieLeave = () => {
        setActiveIndex(null);
    };

    // Active shape rendering for hover effect
    const renderActiveShape = (props) => {
        const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
        
        return (
            <g>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius + 5}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
            </g>
        );
    };

    // Render the percentage label inside the pie chart
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
        // Only show labels for sections with enough space (more than 5%)
        if (percent < 0.05) return null;
        
        // For the "Other" category, don't show a label
        if (name === 'Other') return null;

        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text 
                x={x} 
                y={y} 
                fill="white" 
                textAnchor="middle" 
                dominantBaseline="central"
                fontWeight="bold"
                fontSize={14}
                stroke="#000"
                strokeWidth={0.5}
                paintOrder="stroke"
                aria-label={`${name}: ${(percent * 100).toFixed(0)}% (${value} mentions)`}
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    // Handle legend item hover
    const handleLegendMouseEnter = (index) => {
        setActiveIndex(index);
    };

    const handleLegendMouseLeave = () => {
        setActiveIndex(null);
    };

    // Toggle competitor visibility
    const handleLegendClick = (competitor) => {
        setHiddenCompetitors(prev => {
            const newHidden = new Set(prev);
            if (newHidden.has(competitor)) {
                newHidden.delete(competitor);
            } else {
                newHidden.add(competitor);
            }
            return newHidden;
        });
    };

    // Legend item component with active state and click handler
    const LegendItem = ({ entry, index }) => {
        const isActive = index === activeIndex;
        const isHidden = hiddenCompetitors.has(entry.competitor_name);
        
        return (
            <div 
                key={`legend-item-${index}`} 
                className={`legend-item ${isActive ? 'legend-item-active' : ''} ${isHidden ? 'legend-item-hidden' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => handleLegendClick(entry.competitor_name)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleLegendClick(entry.competitor_name);
                    }
                }}
                onMouseEnter={() => handleLegendMouseEnter(index)}
                onMouseLeave={handleLegendMouseLeave}
                style={{ cursor: 'pointer' }}
            >
                <div
                    className="legend-color"
                    style={{
                        // Restore background color
                        backgroundColor: entry.competitor_name === 'Other' ? 
                             OTHER_COLOR : COLORS[index % COLORS.length],
                        // Remove border
                        // border: '2px solid red'
                    }}
                    aria-hidden="true"
                />
                <div className="legend-text" /* style={{ opacity: isHidden ? 0.5 : 1 }} */ >
                    {entry.competitor_name}
                </div>
                <div className="legend-percentage" /* style={{ opacity: isHidden ? 0.5 : 1 }} */ >
                    {entry.mention_percentage.toFixed(1)}% ({entry.mention_count})
                </div>
            </div>
        );
    };

    // Update totalMentions to use visible mentions only
    const totalMentions = useMemo(() => {
        return visibleCompetitorMentions.reduce((sum, item) => sum + item.mention_count, 0);
    }, [visibleCompetitorMentions]);

    // Log when topics change to understand the structure
    useEffect(() => {
        // console.log('Topics structure:', topics);
        if (topics?.[0]) {
            // console.log('First topic:', topics[0]);
        }
    }, [topics]);

    // Log when selectedProduct changes
    useEffect(() => {
        // console.log('Selected product changed to:', selectedProduct);
        // Find the selected topic to verify the structure
        const selectedTopic = topics?.find(t => t.id === selectedProduct);
        // console.log('Selected topic:', selectedTopic);
    }, [selectedProduct, topics]);

    // Determine what to render based on state
    const renderContent = () => {
        if (loading) {
            return (
                <div className="loading-container">
                    <CircularProgress size={60} thickness={4} />
                    <p className="loading-text">Loading competitor mentions...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="error-message">
                    <p>{error}</p>
                </div>
            );
        }

        if (!selectedProduct) {
            return (
                <div className="no-data-message">
                    Please select a topic to view competitor mentions.
                </div>
            );
        }

        if (competitorMentions.length === 0 && !initialLoad) {
            return (
                <div className="no-data-message">
                    No competitor mentions found for the selected topic.
                </div>
            );
        }
        
        return (
            <div className="chart-container">
                <div className="chart-with-legend">
                    {/* Chart Section */}
                    <div className="pie-chart-container">
                        <PieChart width={400} height={400} role="img" aria-label="Competitor mentions pie chart">
                            {/* Donut chart with inner radius */}
                            <Pie
                                data={sortedCompetitorMentions}
                                cx={200}
                                cy={200}
                                labelLine={false}
                                outerRadius={150}
                                innerRadius={70} 
                                fill="#8884d8"
                                dataKey="mention_count"
                                nameKey="competitor_name"
                                label={renderCustomizedLabel}
                                paddingAngle={1}
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                onMouseEnter={onPieEnter}
                                onMouseLeave={onPieLeave}
                            >
                                {sortedCompetitorMentions.map((entry, index) => (
                                    <Cell 
                                        key={`cell-${index}`} 
                                        fill={entry.competitor_name === 'Other' ? OTHER_COLOR : COLORS[index % COLORS.length]} 
                                        stroke="#fff"
                                        strokeWidth={1}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                        
                        {/* Total Mentions Label */}
                        {totalMentions > 0 && (
                            <div className="chart-center-label">
                                <div className="total-mentions">{totalMentions}</div>
                                <div className="total-mentions-label">Total Mentions</div>
                            </div>
                        )}
                    </div>
                    
                    {/* Legend */}
                    <div className="custom-legend" role="list" aria-label="Chart legend">
                        {sortedCompetitorMentions.map((entry, index) => (
                            <LegendItem 
                                key={`legend-item-${index}`}
                                entry={entry} 
                                index={index} 
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="competitor-mentions-container">
            {/* <style>
                {`
                    .legend-item-hidden .legend-color {
                        opacity: 0.3;
                    }
                    .legend-item-hidden .legend-text,
                    .legend-item-hidden .legend-percentage {
                        opacity: 0.5;
                    }
                    .legend-item {
                        transition: opacity 0.2s ease;
                        display: flex;
                        align-items: center;
                        padding: 6px 8px;
                        margin: 2px 0;
                        border-radius: 4px;
                    }
                    .legend-item:hover {
                        background-color: rgba(0, 0, 0, 0.04);
                    }
                    .legend-color {
                        width: 16px;
                        height: 16px;
                        border-radius: 4px;
                        margin-right: 12px;
                        flex-shrink: 0;
                    }
                    .legend-text {
                        font-size: 16px;
                        font-weight: 500;
                        color: #333;
                    }
                    .legend-percentage {
                        font-size: 14px;
                        color: #666;
                    }
                    .custom-legend {
                        padding: 15px;
                        margin-left: 20px;
                    }
                    .chart-container {
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                        padding: 20px;
                    }
                    .chart-with-legend {
                        display: flex;
                        align-items: flex-start;
                        max-width: 800px;
                    }
                    .pie-chart-container {
                        position: relative;
                        width: 400px;
                        height: 400px;
                    }
                    .chart-center-label {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        text-align: center;
                    }
                    .total-mentions {
                        font-size: 36px;
                        font-weight: bold;
                        color: #333;
                    }
                    .total-mentions-label {
                        font-size: 14px;
                        color: #666;
                    }
                `}
            </style> */}
            <div className="controls">
                {Object.keys(filteredTopicMap).length > 0 && (
                    <ProductSelector
                        topicMap={filteredTopicMap}
                        selectedId={selectedProduct}
                        onSelect={setSelectedProduct}
                        className="product-selector"
                        label="Topic"
                    />
                )}
                <TimeRangeSelector
                    timeRange={timeRange}
                    onTimeRangeChange={setTimeRange}
                    periods={['1y', '3y', '5y', 'all']}
                    aria-label="Select time range"
                />
            </div>

            {renderContent()}
        </div>
    );
};

export default CompetitorMentionsChart; 